import TUIChatEngine, {
  TUITranslateService,
  TUIStore,
  StoreName,
  IMessageModel,
  TUIUserService,
  IConversationModel,
  TUIChatService,
} from '../@aidesk/uikit-engine';
import Log from './logger';
import { isCustomerServiceMessage, JSONToObject } from './index';
import { CUSTOM_MESSAGE_SRC } from '../constant';
import { Marked } from 'marked';

export function deepCopy(data: any, hash = new WeakMap()) {
  if (typeof data !== 'object' || data === null || data === undefined) {
    return data;
  }
  if (hash.has(data)) {
    return hash.get(data);
  }
  const newData: any = Object.create(Object.getPrototypeOf(data));
  const dataKeys = Object.keys(data);
  dataKeys.forEach((value) => {
    const currentDataValue = data[value];
    if (typeof currentDataValue !== 'object' || currentDataValue === null) {
      newData[value] = currentDataValue;
    } else if (Array.isArray(currentDataValue)) {
      newData[value] = [...currentDataValue];
    } else if (currentDataValue instanceof Set) {
      newData[value] = new Set([...currentDataValue]);
    } else if (currentDataValue instanceof Map) {
      newData[value] = new Map([...currentDataValue]);
    } else {
      hash.set(data, data);
      newData[value] = deepCopy(currentDataValue, hash);
    }
  });
  return newData;
}

export const handleSkeletonSize = (
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } => {
  const widthToHeight = width / height;
  const maxWidthToHeight = maxWidth / maxHeight;
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }
  if (
    (width <= maxWidth && height > maxHeight)
    || (width > maxWidth && height > maxHeight && widthToHeight <= maxWidthToHeight)
  ) {
    return { width: width * (maxHeight / height), height: maxHeight };
  }
  return { width: maxWidth, height: height * (maxWidth / width) };
};

const IMAGE_EXTENSIONS = ['gif', 'jpeg', 'jpg', 'png', 'bmp', 'webp'];
const VIDEO_EXTENSIONS = ['mov', 'mp4'];

export function getMediaTypeByUrl(url?: string, name?: string): 'image' | 'video' | 'file' {
  const source = name || url || '';
  if (!source) {
    return 'file';
  }

  const pathname = source.split(/[?#]/)[0];
  const dotIndex = pathname.lastIndexOf('.');
  if (dotIndex < 0) {
    return 'file';
  }
  const ext = pathname.slice(dotIndex + 1).toLowerCase();
  if (IMAGE_EXTENSIONS.includes(ext)) {
    return 'image';
  }
  if (VIDEO_EXTENSIONS.includes(ext)) {
    return 'video';
  }
  return 'file';
}

// Image loading complete
export function getImgLoad(container: any, className: string, callback: any) {
  const images = container?.querySelectorAll(`.${className}`) || [];
  const promiseList = Array.prototype.slice.call(images).map((node: any) => {
    return new Promise((resolve: any) => {
      node.onload = () => {
        resolve(node);
      };
      node.onloadeddata = () => {
        resolve(node);
      };
      node.onprogress = () => {
        resolve(node);
      };
      if (node.complete) {
        resolve(node);
      }
    });
  });
  return Promise.all(promiseList)
    .then(() => {
      callback && callback();
    })
    .catch((e) => {
      console.error('网络异常', e);
    });
}

export const isCreateGroupCustomMessage = (message: IMessageModel) => {
  return (
    message.type === TUIChatEngine.TYPES.MSG_CUSTOM
    && message?.getMessageContent()?.businessID === 'group_create'
  );
};

/**
 * displayMessageReadReceipt: User-level control to display message read status
 * After turning off, the messages you send and receive will not have message read status
 * You will not be able to see whether the other party has read their messages, and they will also not be able to see whether you have read the messages they sent
 *
 * enabledMessageReadReceipt: App-level setting to enable read receipts
 * @return {boolean} - Returns a boolean value indicating if the message read receipt is enabled globally.
 */
export function isEnabledMessageReadReceiptGlobal(): boolean {
  return TUIStore.getData(StoreName.USER, 'displayMessageReadReceipt')
    && TUIStore.getData(StoreName.APP, 'enabledMessageReadReceipt');
}

export function shallowCopyMessage(message: IMessageModel) {
  return Object.assign({}, message);
}

// calculate timestamp
export function calculateTimestamp(timestamp: number): string {
  const todayZero = new Date().setHours(0, 0, 0, 0);
  const thisYear = new Date(
    new Date().getFullYear(),
    0,
    1,
    0,
    0,
    0,
    0,
  ).getTime();
  const target = new Date(timestamp);

  const oneDay = 24 * 60 * 60 * 1000;
  const oneWeek = 7 * oneDay;

  const diff = todayZero - target.getTime();

  function formatNum(num: number): string {
    return num < 10 ? '0' + num : num.toString();
  }

  if (diff <= 0) {
    // today, only display hour:minute
    return `${formatNum(target.getHours())}:${formatNum(target.getMinutes())}`;
  } else if (diff <= oneDay) {
    // yesterday, display yesterday:hour:minute
    return `${TUITranslateService.t('time.昨天')} ${formatNum(
      target.getHours(),
    )}:${formatNum(target.getMinutes())}`;
  } else if (diff <= oneWeek - oneDay) {
    // Within a week, display weekday hour:minute
    const weekdays = [
      '星期日',
      '星期一',
      '星期二',
      '星期三',
      '星期四',
      '星期五',
      '星期六',
    ];
    const weekday = weekdays[target.getDay()];
    return `${TUITranslateService.t('time.' + weekday)} ${formatNum(
      target.getHours(),
    )}:${formatNum(target.getMinutes())}`;
  } else if (target.getTime() >= thisYear) {
    // Over a week, within this year, display mouth/day hour:minute
    return `${target.getMonth() + 1}/${target.getDate()} ${formatNum(
      target.getHours(),
    )}:${formatNum(target.getMinutes())}`;
  } else {
    // Not within this year, display year/mouth/day hour:minute
    return `${target.getFullYear()}/${
      target.getMonth() + 1
    }/${target.getDate()} ${formatNum(target.getHours())}:${formatNum(
      target.getMinutes(),
    )}`;
  }
}

export function getSafeUrl(url) {
  try {
    const decodedUrl = decodeURIComponent(url);
    const parsedUrl = new URL(decodedUrl);
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return null;
    }

    // 清除 username 和 password
    parsedUrl.username = '';
    parsedUrl.password = '';
    return parsedUrl.href;
  } catch (e) {
    return null;
  }
}

export function openSafeUrl(content: string) {
  const safeUrl = getSafeUrl(content);
  if (safeUrl) {
    window.open(safeUrl, '_blank', 'noopener,noreferrer');
  } else {
    Log.w(`Invalid URL provided:${content}`);
  }
}

// call after chat engine is ready
export function switchReadStatus(value: number) {
  if (value !== 1) {
    TUIUserService.switchMessageReadStatus(false);
  } else {
    TUIUserService.switchMessageReadStatus(true);
  }
}

export function getTo(conversation: IConversationModel): string {
  return conversation?.groupProfile?.groupID || conversation?.userProfile?.userID;
}

export function isNonEmptyObject(obj: any): boolean {
  if (obj && Object.getPrototypeOf(obj) === Object.prototype && Object.keys(obj).length > 0) {
    return true;
  }
  return false;
}

export function getQuoteContentForDesk(message: IMessageModel): string {
  let result = TUITranslateService.t('TUIChat.自定义');
  if (isCustomerServiceMessage(message)) {
    const payload = JSONToObject(message.payload.data);
    const src = payload.src;
    if (src === CUSTOM_MESSAGE_SRC.BRANCH || src === CUSTOM_MESSAGE_SRC.BRANCH_NUMBER) {
      result = payload.content.header;
    } else if (src === CUSTOM_MESSAGE_SRC.RICH_TEXT) {
      result = parseQuoteMarkdown(payload.content);
    } else if (src === CUSTOM_MESSAGE_SRC.STREAM_TEXT) {
      const chunks = Array.isArray(payload.chunks) ? payload.chunks.join('') : payload.chunks;
      result = parseQuoteMarkdown(chunks);
    } else if (src === CUSTOM_MESSAGE_SRC.MULTI_BRANCH) {
      result = payload.content.header;
    } else if (src === CUSTOM_MESSAGE_SRC.MULTI_FORM) {
      result = payload.content.tip;
    } else if (src === CUSTOM_MESSAGE_SRC.ROBOT_MSG) {
      result = payload.content.title;
    } else if (src === CUSTOM_MESSAGE_SRC.PRODUCT_CARD) {
      result = payload.content.header || payload.content.desc;
    }
  }
  
  return result;
}

const quoteMarked = new Marked(
  {mangle: false, headerIds: false},
  {
    renderer: {
      image(this: any, href: string | null, title: string | null, text: string) {
        return TUITranslateService.t('TUIChat.图片');
      },
      link(this: any, href: string | null, title: string | null, text: string) {
        if (href) {
          return TUITranslateService.t('TUIChat.链接');
        }
        return text;
      },
    },
  },
);

function parseQuoteMarkdown(content: string): string {
  let result = quoteMarked.parse(content);
  result = typeof result === 'string' ? result : '';
  result = result.replace(/\n+/g, ' ').trim()
                  .replace(/\*\*|\*|_|`|#/g, '')
                  .replace(/<[^>]+>/g, '');
  return result;
}

export function transferToTaskFlow(conversationID: string, taskFlowID: number, description?: string) {
  if (!taskFlowID) {
    Log.w(`taskFlowID is required`);
    return;
  }
  TUIChatService.sendCustomMessage({
    to: conversationID,
    conversationType: TUIChatEngine.TYPES.CONV_C2C,
    payload: {
      data: JSON.stringify({
        src: CUSTOM_MESSAGE_SRC.TRANSFER_TO_TASK_FLOW,
        customerServicePlugin: 0,
        taskId: taskFlowID,
        env: 'production',
        description: description || '',
      })
    }
  },{ onlineUserOnly: description ? false : true });
}

export function transferToHuman(conversationID: string, groupID?: number, specificMemberList?: Array<string>, description?: string) {
  if (!groupID && (!specificMemberList || specificMemberList.length === 0)) {
    Log.w(`groupID or specificMemberList is required`);
    return;
  }
  TUIChatService.sendCustomMessage({
    to: conversationID,
    conversationType: TUIChatEngine.TYPES.CONV_C2C,
    payload: {
      data: JSON.stringify({
        src: CUSTOM_MESSAGE_SRC.TRANSFER_TO_HUMAN,
        customerServicePlugin: 0,
        groupId: groupID || 0,
        specificMemberList: specificMemberList || [],
        description: description || '',
      })
    }
  },{ onlineUserOnly: description ? false : true });
}

const MessageTypeList = [
  'standardTaskFlowBranchOption', // 标准任务流分支选项。
  'standardTaskFlowInformationCollection', // 标准任务流信息收集。
  'standardTaskFlowReplyMessage', // 标准任务流回复消息。
  'fallback', // 兜底消息。
  'faq', // 机器人问答库回复消息（命中问答库）。
  'clarify', // 机器人澄清消息（引导提问）。
  'chitchat', // 机器人闲聊（欢迎卡片及命中寒暄库）。
  'rag', // 机器人文档检索回复消息（命中文档问答）。
  'aiTaskFlowInformationCollection', // 机器人智能任务流信息收集。
  'aiTaskFlowEndReply', // 机器人智能任务流结束回复。
  'aiReply', // ai回复（大模型模式下命中大模型回答）。
];

const AINoteMessageTypeList = [
  'fallback',
  'faq',
  'aiReply',
];

export function canShowFeedback(cloudCustomData: string): boolean {
  try {
    const jsonObj = JSONToObject(cloudCustomData);
    return MessageTypeList.includes(jsonObj.messageType) && jsonObj.role === "robot";
  } catch (e) {
    return false;
  }
}

export function canShowAINote(cloudCustomData: string): boolean {
  try {
    const jsonObj = JSONToObject(cloudCustomData);
    return AINoteMessageTypeList.includes(jsonObj.messageType) && jsonObj.role === "robot";
  } catch (e) {
    return false;
  }
}

export function validateUserID(userID: string) {
  let ret = 0;
  if (!/^[\x20-\x7E]+$/.test(userID)) {
    ret = 1;
  }
  if (userID.toLowerCase().includes('administrator')) {
    ret = 2;
  }
  const byteSize = new TextEncoder().encode(userID).length;
  if (byteSize > 45) {
    ret = 3;
  }
  return ret;
}

export function updateCustomStore(key: string, data: {conversationID: string, value: any}) {
  TUIStore.update(StoreName.CUSTOM, key, data);
}

export function debounce(func, delay) {
  let timeoutID;
  return function (this: any, ...args) {
    const context = this;
    if (timeoutID) {
      clearTimeout(timeoutID);
    }
    timeoutID = setTimeout(() => {
      func.apply(context, args); 
    }, delay);
  }
}

export function leaveQueuing(conversationID: string) {
  TUIChatService.sendCustomMessage({
    to: conversationID,
    conversationType: TUIChatEngine.TYPES.CONV_C2C,
    payload: {
      data: JSON.stringify({
        customerServicePlugin: 0,
        src: CUSTOM_MESSAGE_SRC.USER_END_CONVERSATION,
      }),
    },
    needReadReceipt: isEnabledMessageReadReceiptGlobal(),
  },{ onlineUserOnly: true });
};

export function throttle(
  func,
  wait = 200,
  options?: { leading?: boolean;}
) {
  let last = 0;
  let timeout;
  const leading = options?.leading !== undefined ? options.leading : true;

  return function (this: any, ...args: any[]) {
    const now = Date.now();
    if (!last && !leading) {
      last = now;
    }

    const remaining = wait - (now - last);

    if (remaining <= 0) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      last = now;
      func.apply(this, args);
    }
  };
}

export function getStyledATagFromText(text, className, linkColor, title) {
  // 开启 url 识别时先移除 html 标签，避免 xss 漏洞
  // 预处理文本：移除换行符和制表符
  const preprocessedText = text.replace(/[\n\t]/g, '').trim();

  // 先移除HTML标签避免XSS漏洞
  const sanitizedText = preprocessedText.replace(/<[^>]+>/g, '');

  // 匹配URL：从http://或https://开始，匹配尽可能长的有效URL字符序列
  // 包括：字母数字、-._~:/?#[]@!$&'()*+,;=% 等URL允许的字符
  const ret = sanitizedText.replace(/https?:\/\/[\w\-._~:/?#\[\]@!$&'()*+,;=%]+/g, (matchedUrl) => {
    // 检查URL是否包含中文字符，如果包含则跳过处理
    const chineseRegex = /[\u4e00-\u9fff]/;
    if (chineseRegex.test(matchedUrl)) {
      return matchedUrl; // 不处理包含中文的URL
    }

    const encodedUrl = encodeURI(matchedUrl);
    return `<a target="_blank" rel="noreferrer noopener" class="${className}" style="color:${linkColor}; text-decoration:underline" href="${encodedUrl}" title="${title || ''}">${title ? title : matchedUrl}</a>`;
  });
  return ret;
}